import { FC, useState, useEffect } from 'react';
import { Modal, Form, Input, Space } from 'antd';
import { RecordGroup, FieldQueryCondition } from '#types/System.types';
import { DESCRIPTION_LIMITS, MIN_LENGTHS } from '#types/System.Parameters.types';

const { TextArea } = Input;

interface RecordGroupModalProps {
    visible: boolean;
    editingGroup?: { id: string; group: RecordGroup } | null;
    existingGroups?: { [key: string]: RecordGroup };
    onSave: (groupId: string, group: RecordGroup) => void;
    onCancel: () => void;
}

/**
 * Modal for creating and editing record groups.
 * Handles form validation, group ID generation, and query condition management.
 */
export const RecordGroupModal: FC<RecordGroupModalProps> = ({
    visible,
    editingGroup,
    existingGroups = {},
    onSave,
    onCancel
}) => {
    const [form] = Form.useForm();
    const [queryConditions, setQueryConditions] = useState<FieldQueryCondition[]>([]);

    useEffect(() => {
        if (visible) {
            if (editingGroup) {
                form.setFieldsValue({
                    shortLabel: editingGroup.group.description?.shortLabel || '',
                    longDescription: editingGroup.group.description?.longDescription || ''
                });
                setQueryConditions(editingGroup.group.queryConditions || []);
            } else {
                const defaultCondition: FieldQueryCondition = {
                    fieldPath: 'entityGroupIds',
                    operator: 'array-contains',
                    value: ''
                };
                setQueryConditions([defaultCondition]);
                form.resetFields();
            }
        }
    }, [visible, editingGroup, form]);

    /**
     * Sanitizes a string for use in group IDs by removing special characters
     * and replacing spaces with underscores
     */
    const sanitizeForId = (str: string): string => {
        return str
            .toUpperCase()
            .replace(/[^A-Z0-9\s]/g, '') // Remove special characters
            .replace(/\s+/g, '_') // Replace spaces with underscores
            .replace(/^_+|_+$/g, '') // Trim underscores from start/end
            .substring(0, 50); // Limit length for ID
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const { shortLabel, longDescription } = values;
            
            let groupId = editingGroup?.id;
            if (!groupId) {
                const randomString = Math.random().toString(36).substring(2, 10);
                const sanitizedLabel = sanitizeForId(shortLabel);
                groupId = `${randomString}_${sanitizedLabel}`;
            }

            const updatedConditions = queryConditions.map(condition => {
                if (condition.fieldPath === 'entityGroupIds' && 
                    condition.operator === 'array-contains' && 
                    !condition.value) {
                    return { ...condition, value: groupId };
                }
                return condition;
            });

            const group: RecordGroup = {
                description: {
                    shortLabel,
                    longDescription
                },
                queryConditions: updatedConditions
            };

            onSave(groupId!, group);
            form.resetFields();
            setQueryConditions([]);
        } catch (error) {
            // Form validation failed - error will be displayed by form UI
            // No need to throw or log, as the form will show validation errors
        }
    };

    return (
        <Modal
            title={editingGroup ? 'Edit Record Group' : 'Create Record Group'}
            open={visible}
            onOk={handleSave}
            onCancel={onCancel}
            width={600}
            okText={editingGroup ? 'Update' : 'Create'}
        >
            <Space vertical style={{ width: '100%' }} size="large">
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="shortLabel"
                        label="Name"
                        rules={[
                            { required: true, message: 'Please enter a short label' },
                            { min: MIN_LENGTHS.shortText, message: `Group name must be at least ${MIN_LENGTHS.shortText} characters` },
                            { max: DESCRIPTION_LIMITS.shortLabel, message: `Group name must not exceed ${DESCRIPTION_LIMITS.shortLabel} characters` },
                            {
                                pattern: /^[a-zA-Z0-9\s\-_.,()'&]+$/,
                                message: 'Group name can only contain letters, numbers, spaces, and common punctuation'
                            },
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    
                                    // Check for duplicate names (case-insensitive)
                                    const normalizedValue = value.trim().toLowerCase();
                                    const isDuplicate = Object.entries(existingGroups).some(([id, group]) => {
                                        // Skip if we're editing the same group
                                        if (editingGroup && id === editingGroup.id) return false;
                                        const existingLabel = group.description?.shortLabel?.trim().toLowerCase();
                                        return existingLabel === normalizedValue;
                                    });
                                    
                                    if (isDuplicate) {
                                        return Promise.reject(new Error('A record group with this name already exists'));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input 
                            maxLength={DESCRIPTION_LIMITS.shortLabel}
                            placeholder="e.g., Electrical Contractors"
                            aria-label="Record group short label"
                        />
                    </Form.Item>

                    <Form.Item
                        name="longDescription"
                        label="Description"
                        rules={[
                            { required: true, message: 'Please enter a description' },
                            { min: MIN_LENGTHS.longText, message: `Description must be at least ${MIN_LENGTHS.longText} characters` },
                            { max: DESCRIPTION_LIMITS.longDescription, message: `Description must not exceed ${DESCRIPTION_LIMITS.longDescription} characters` }
                        ]}
                    >
                        <TextArea
                            rows={3}
                            maxLength={DESCRIPTION_LIMITS.longDescription}
                            placeholder="e.g., Electrical contracting firms that purchase and install lighting fixtures and control systems"
                            aria-label="Record group long description"
                        />
                    </Form.Item>
                </Form>
            </Space>
        </Modal>
    );
};